import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from "@shared/store/dialog.duck";
import { Content } from '@shared/dialogs/DayNotesEntryDialog';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { smallTablet } from '@shared/ui/atoms/MediaQueries';
import Dialog from '@shared/ui/molecules/Dialog';
import styled from "styled-components";
import TextField from '@shared/ui/atoms/TextField';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { HospitalityOrderItem } from "../../../../meeting-rooms/models/hospitality.models";
import {
  createNewOrderItem,
  deleteOrderItem,
  selectIsLoading,
  updateOrderItem
} from "../data/hospitality-management.duck";
import { useTranslation } from "react-i18next";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Colours from '@shared/ui/atoms/Colours';
import CreatableSelect from "react-select/creatable";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';

const OPTIONS = [{label: 'GBP', value: '£'}]

export function HospitalityCreateOrderItemDialog(props: Props) {
  const {t} = useTranslation();
  const {orderItem} = props.payload;
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const loading = useSelector(selectIsLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    if (orderItem && orderItem.id) {
      setLabel(orderItem.label);
      setDescription(orderItem.description);
      setPrice(orderItem.price.toString());
    }
  }, [orderItem]);

  const close = () => {
    dispatch(closeDialog());
  }

  const save = async () => {
    if (!props.payload.orderItem) {
      const orderItem: any = {
        officeId: props.payload.office.id,
        label: label,
        description: description,
        enabled: false,
        price: parseFloat(price),
        currency: '£',
      }
      dispatch(createNewOrderItem({
        orderItem: orderItem,
        office: props.payload.office
      }))
    } else {
      const orderItemToUpdate = {
        ...orderItem,
        label: label,
        description: description,
        price: parseFloat(price),
        currency: '£',
      }
      dispatch(updateOrderItem({
        orderItem: orderItemToUpdate,
        office: props.payload.office
      }))
    }
  }

  const deleteItem = async () => {
    if (props.payload.orderItem) {
      dispatch(deleteOrderItem({
        orderItem: props.payload.orderItem,
        office: props.payload.office,
      }))
    }
  }

  return (
    <DialogWrapper isOpen={true} onClose={close}>
      <HeadlineMedium style={{marginBottom: 16}}>{t('settings.manage-hospitality.add-hospitality-item')}</HeadlineMedium>
      <Content>
        <FlatContentCard>
          <Icon icon={IconTypes.Office} />
          <BodyRegular weight={600}>{props.payload.office.label}</BodyRegular>
        </FlatContentCard>
        <TextField onChange={setLabel} disabled={loading} fullWidth={true} label={'settings.manage-hospitality.item-name'} value={label} />
        <TextField onChange={setDescription} type={'text'} disabled={loading} fullWidth={true} label={'settings.manage-hospitality.notes'} value={description} />

        <BodyRegular weight={600} className="textField__leftText">{t('settings.manage-hospitality.currency')}</BodyRegular>
        <CreatableSelect
          options={OPTIONS}
          onChange={() => {}}
          value={OPTIONS[0]}
          styles={REACT_SELECT_DROPDOWN_STYLES}
          closeMenuOnSelect={true}
          isMulti={false}
          isLoading={loading}
        />

        <TextField style={{marginTop: 24}}
                   onChange={setPrice}
                   disabled={loading}
                   fullWidth={true}
                   label={'settings.manage-hospitality.price'}
                   value={price} />

        <PrimaryButton style={{margin: '24px auto 0 auto'}}
                       text={`button.save`}
                       disabled={loading}
                       click={save}
                       fullWidth={true} />
        {props.payload.orderItem?.id && <>
          <OutlineButton style={{margin: '24px auto 0 auto'}}
                         text={`button.delete`}
                         disabled={loading}
                         click={deleteItem}
                         fullWidth={true} />
        </>}
      </Content>
    </DialogWrapper>
  )
}

interface Props {
  payload: {
    office: OfficeEntity;
    orderItem?: HospitalityOrderItem;
  };
}


const DialogWrapper = styled(Dialog)<any>`
  &.dialog {
    width: 100vw;
    height: 100vh;
    padding: 20px 0 0 0;
    @media (${smallTablet}) {
      padding: 64px 48px;
      max-width: 100%;
      min-height: 400px;
      width: unset;
      height: unset;
    }
  }
  .dialog__contentContainer, .dialog__content {
    width: 100%;
    padding-top: 0;
    @media (${smallTablet}) {
      width: 400px;
      min-height: 410px;
    }
  }
  .dialog__content {
    display: flex;
    flex-direction: column;
  }
  .textField__label {
    color: ${Colours.black} !important;
  }
`
const FlatContentCard = styled.div`
  padding: 12px 24px;
  background-color: ${Colours.veryLightGrey};
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  i {
    margin-right: 16px;
  }
`
